<template>
  <div class="menu-content-student">
    <div v-if="hasRoutes" class="menu-routes">
      <slot name="routes" :routes="displayRoutes" :is-expanded="isExpanded">
        <UserRoutesGrid :user-routes="displayRoutes" :is-expanded="isExpanded" />
      </slot>
    </div>

    <div v-if="loading" class="loading-state">
      <IonSpinner name="crescent" size="small" />
    </div>

    <div
      v-if="!loading"
      class="menu-categories"
      :class="{ 'menu-categories-expanded': isExpanded }"
    >
      <slot name="categories" :categories="modulesWithLinks" :is-expanded="isExpanded">
        <CategoriesList
          v-if="modulesWithLinks.length > 0"
          :categories="modulesWithLinks"
          :is-open="isExpanded"
        />
      </slot>
    </div>

    <div v-if="showQuickSearch && isExpanded" class="menu-search">
      <slot name="search" :routes="displayRoutes" :categories="modulesWithLinks"> </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IonSpinner } from '@ionic/vue';
import { useModuleApi } from '@/features/menu-management/composables/useModuleApi';
import { useUserRoutes } from '@/common/composables/useUserRoutes';
import { useLayout } from '@/layouts/composables/useLayout';
import UserRoutesGrid from '@/layouts/components/UserRoutesGrid.vue';
import CategoriesList from '@/layouts/components/category-collapse/CategoriesList.vue';

interface Props {
  isExpanded: boolean;
}

const props = defineProps<Props>();

// Composables
const { modulesWithLinks, loading } = useModuleApi();
const { displayRoutes, hasRoutes } = useUserRoutes();
const { shouldShowQuickSearch } = useLayout();

// Computed
const showQuickSearch = computed(() => shouldShowQuickSearch.value);
</script>

<style scoped>
.menu-content-student {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.menu-routes {
  flex-shrink: 0;
}

.menu-categories {
  flex: 1;
  padding: 0 10px;
  scrollbar-width: thin;
  scrollbar-color: var(--ion-color-medium) transparent;
  overflow: hidden;
}

.menu-categories-expanded {
  overflow-y: auto;
  overflow-x: hidden;
}

.menu-categories::-webkit-scrollbar {
  width: 4px;
}

.menu-categories::-webkit-scrollbar-track {
  background: transparent;
}

.menu-categories::-webkit-scrollbar-thumb {
  background-color: var(--ion-color-medium);
  border-radius: 2px;
}

.menu-categories::-webkit-scrollbar-thumb:hover {
  background-color: var(--ion-color-medium-shade);
}

.menu-search {
  padding: 10px;
  border-top: 1px solid var(--ion-border-color);
  flex-shrink: 0;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
}
</style>
